"use client"

import { useState, useEffect } from "react"
import type { ApiResponse } from "../types/quiz"

const BACKEND_URL = ""

interface BackendApiResponse<T> {
  data: T | null
  loading: boolean
  error: string | null
}

export function useBackendApi<T>(endpoint: string, dependencies: any[] = []): BackendApiResponse<T> {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // Don't make request if endpoint is empty
    if (!endpoint || endpoint.trim() === "") {
      setLoading(false)
      setData(null)
      setError(null)
      return
    }

    const fetchData = async () => {
      try {
        setLoading(true)
        setError(null)

        const token = localStorage.getItem("auth_token")
        const headers: HeadersInit = {
          "Content-Type": "application/json",
        }

        if (token) {
          headers.Authorization = `Bearer ${token}`
        }

        const response = await fetch(`${BACKEND_URL}${endpoint}`, {
          headers,
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const result: ApiResponse<T> = await response.json()

        if (result.success) {
          setData(result.data || null)
        } else {
          const errorMessage = result.error || result.message || "An error occurred"
          setError(errorMessage)
          console.error("Backend API Error:", errorMessage)
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "An error occurred"
        setError(errorMessage)
        console.error("Backend API Error:", err)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, dependencies)

  return { data, loading, error }
}

export async function makeBackendRequest<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
  const token = localStorage.getItem("auth_token")
  const headers: HeadersInit = {
    "Content-Type": "application/json",
    ...options.headers,
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`
  }

  const response = await fetch(`${BACKEND_URL}${endpoint}`, {
    ...options,
    headers,
  })

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }

  const result: ApiResponse<T> = await response.json()

  if (!result.success) {
    const errorMessage = result.error || result.message || "Request failed"
    throw new Error(errorMessage)
  }

  return result
}

// Helper function for extracting data from API responses
export async function makeBackendRequestData<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const response = await makeBackendRequest<T>(endpoint, options)
  if (!response.data) {
    throw new Error("No data received from server")
  }
  return response.data
}
